import clsx from "clsx";
import { type ComponentProps, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Breadcrumbs } from "@features/navigations/breadcrumbs";

import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { MediaBackground } from "@shared/ui/media-background";
import { SwiperNavButtons } from "@shared/ui/slider-nav-buttons";
import { Body, Heading } from "@shared/ui/typography";
import {
  ApplyReplacements,
  mapItemsToPositionedIcons,
} from "@shared/ui/typography/apply-replacements-to-text";

import type { HeroItemType } from "../../model/types";

import s from "./hero.module.scss";

export type CompProps = ComponentProps<"div"> & {
  data: HeroItemType[];
};

export const Hero = (props: CompProps) => {
  const { data } = props;

  const $swiper = useRef<SwiperType | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className={clsx(s.root)}>
      <div className={s.front}>
        <div>
          <Breadcrumbs className={s.breadcrumbs} />
        </div>
        {data.length > 1 && (
          <SwiperNavButtons
            swiperRef={$swiper}
            variant="light"
            buttonType="secondary"
            totalSlides={data.length}
            currentIndex={activeIndex}
            className={s.buttons}
          />
        )}
      </div>
      <Swiper
        modules={[Autoplay, EffectFade, Pagination]}
        pagination={{
          el: `.${s.pagination}`,
          type: "bullets",
          clickable: true,
          bulletClass: `${s.bullet}`,
          bulletActiveClass: `${s.bulletActive}`,
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        className={s.slider}
        loop
        effect="fade"
        fadeEffect={{ crossFade: true }}
        onSlideChange={({ realIndex }) => setActiveIndex(realIndex)}
        onInit={(swiper) => {
          $swiper.current = swiper;
        }}
      >
        {data.map((item, index) => (
          <SwiperSlide key={`slide-item-${index + 1}`} className={s.slide}>
            <MediaBackground media={item.backgroundImage} className={s.image} />
            <div className={s.slideContent}>
              {item.titleWithIcon?.text && (
                <Heading level={2} tag="h1" className={s.title}>
                  <ApplyReplacements
                    text={item.titleWithIcon?.text}
                    settings={mapItemsToPositionedIcons(
                      item.titleWithIcon?.titleIconSettings,
                    )}
                  />
                </Heading>
              )}
              <Body className={s.description} size="caption">
                {item.description}
              </Body>
              {item?.button?.text && (
                <ButtonWithIcon
                  href={item.button.link}
                  iconName={item?.button?.pageIcon?.icon?.name || "plus"}
                  iconFillColor={item?.button?.pageIcon?.fillColor || "gold"}
                  iconStrokeColor={
                    item?.button?.pageIcon?.strokeColor || "bistre"
                  }
                  iconSvgCode={item?.button?.pageIcon?.uicon?.svg_code}
                  variant="primary-light"
                  className={s.button}
                >
                  {item.button.text}
                </ButtonWithIcon>
              )}
            </div>
          </SwiperSlide>
        ))}
        {data.length > 1 && <div className={s.pagination}></div>}
      </Swiper>
    </section>
  );
};

Hero.displayName = "Hero";
